import { Flex, SelectChangeEventHandler, Box, Checkbox, Text } from '@cad-arc';
import { useCallback, useMemo } from 'react';
import { BaseSelect } from '../../forms/inputs/base-select';
import { Option } from '../../forms/option';

export function QueueFilterMultiselect<T extends string | number>({
    label,
    attributeOptions,
    values,
    onChange,
    dataTestId,
}: {
    label: string;
    attributeOptions: Option<T>[];
    values: Array<string | number>;
    onChange: (updatedValues: Array<string | number>) => void;
    dataTestId?: string;
}): React.ReactElement {
    // add all options corresponding to attributeOptions of this filter type ONLY, while stll keeping selected options from other filter types intact
    const selectAll = useCallback(() => {
        const unselectedOptions = attributeOptions.filter(
            (option) => !values.includes(option.value)
        );
        onChange([...values, ...unselectedOptions.map((option) => option.value)]);
    }, [attributeOptions, onChange, values]);

    // remove all options corresponding to attributeOptions of this filter type ONLY, while still keeping selected options from other filter types intact
    const clearAll = useCallback(() => {
        onChange(
            values.filter(
                (selectedValue) =>
                    !attributeOptions.some((option) => option.value === selectedValue)
            )
        );
    }, [attributeOptions, onChange, values]);

    // check if all options corresponding to attributeOptions of this filter type are currently selected (i.e all options present in values)
    const isSelectingAll = useMemo(
        () =>
            attributeOptions.length > 0 &&
            attributeOptions.every((option) => values.includes(option.value)),
        [values, attributeOptions]
    );

    const handleChange: SelectChangeEventHandler<Option<T>, true> = (selectedOptions) => {
        const allValues: Array<typeof attributeOptions[number]['value']> = attributeOptions.map(
            (option) => option.value
        );
        const updatedValue = values.filter((value) => !allValues.includes(value as T));
        onChange([...updatedValue, ...selectedOptions.currentTarget.value]);
    };

    return (
        <Box mt="4" data-testid={dataTestId}>
            <Flex paddingBottom="7px">
                <Text variant="bodyMd" fontWeight="semibold">
                    {label}
                </Text>
                <Box ml="auto" alignSelf="flex-end">
                    <Checkbox
                        data-testid={`${dataTestId}-select-all-checkbox`}
                        isChecked={isSelectingAll}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.checked) {
                                selectAll();
                            } else {
                                clearAll();
                            }
                        }}
                    >
                        Select All
                    </Checkbox>
                </Box>
            </Flex>
            <Flex wrap="nowrap">
                <BaseSelect
                    isMultiple
                    options={attributeOptions}
                    truncateSelectedCount={2}
                    disableCloseMenuOnScroll
                    onChange={handleChange}
                    value={attributeOptions.filter((option) => values.includes(option.value))}
                ></BaseSelect>
            </Flex>
        </Box>
    );
}
